import { useOrganization, useUser } from '@clerk/clerk-react'
import { useIsGlobalAdmin } from '../hooks/useIsGlobalAdmin'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import type { ReactNode } from 'react'

interface ProtectedGlobalAdminRouteProps {
  children: ReactNode
}

/**
 * Protected route wrapper for global-admin-only pages.
 *
 * Waits for Clerk to load, then denies the page when the user does not hold
 * `org:global_admin` in their active org. On denial: closes the active tab (if
 * a MainTabs context is available), shows a toast, and redirects to "/".
 *
 * Whether the organization has the page at all is a separate question, and one
 * the permissions matrix answers — billing was the only caller that paired this
 * with a feature flag, and it asks `billing.view` now.
 */
export function ProtectedGlobalAdminRoute({
  children,
}: ProtectedGlobalAdminRouteProps) {
  const { isLoaded: userLoaded } = useUser()
  const { isLoaded: orgLoaded } = useOrganization()
  const isGlobalAdmin = useIsGlobalAdmin()

  return (
    <ProtectedRoute
      allowed={userLoaded && orgLoaded ? isGlobalAdmin : undefined}
      message="You need admin access to view this page."
      toastId="admin-access-denied">
      {children}
    </ProtectedRoute>
  )
}
